<template>
  <div v-element-hover="(hovered) => (isCellHovered = hovered)" class="c-saved-canvases-management__owner-cell">
    <NSelect
      v-if="dropdownMode"
      ref="ownerSelectRef"
      bordered
      class="min-w-[250px]"
      :filter="filterUsers"
      filterable
      label-field="name"
      :loading="isFetchingAvailableUsers || isUpdating"
      :options="options"
      :placeholder="t('canvasUI.CSavedCanvasesManagementOwnerCell.selectOwner')"
      :show-checkmark="false"
      :value="props.owner?.value"
      value-field="value"
      @update:value="onUserSelect"
    />
    <template v-else>
      <div v-if="!isEditing && !isUpdating" class="flex items-center">
        <div class="overflow-hidden text-ellipsis whitespace-nowrap min-w-0">{{ owner?.name }}</div>
        <CIcon
          v-if="editable && isCellHovered"
          v-tooltip="t('canvasUI.CSavedCanvasesManagementOwnerCell.selectOwner')"
          class="cursor-pointer ml-2 flex-shrink-0"
          :color="themeVars.primary2"
          fa-type="fa-kit"
          icon="solid-share-circle-user"
          size="18px"
          @click.stop="onEdit"
        />
      </div>
      <NSelect
        v-else
        ref="ownerSelectRef"
        bordered
        class="min-w-[250px]"
        :filter="filterUsers"
        filterable
        label-field="name"
        :loading="isFetchingAvailableUsers || isUpdating"
        :options="options"
        :show-checkmark="false"
        :value="props.owner?.value"
        value-field="value"
        @blur="() => emit('update:is-editing', false)"
        @update:value="onUserSelect"
      />
    </template>
  </div>
</template>

<script lang="ts">
export type Owner = {
  value: User['id']
  name: string
  email: User['email']
}
</script>

<script setup lang="ts">
import { ref, computed, nextTick, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { NSelect, type SelectOption } from 'naive-ui'
import { vElementHover } from '@vueuse/components'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import tooltipDirective from '@lib/directives/tooltip'
import useThemeVars from '@lib/composables/useThemeVars'
import useFetchUsers from '@lib/composables/useFetchUsers'
import type { User } from '@openapi'

const emit = defineEmits<{
  'update:owner': [o: Owner]
  'update:is-editing': [v: boolean]
}>()

const props = withDefaults(
  defineProps<{
    editable?: boolean
    isEditing?: boolean
    isUpdating?: boolean
    myUser?: User | null
    owner?: Owner | null
    dropdownMode?: boolean
  }>(),
  {
    editable: false,
    isEditing: false,
    isUpdating: false,
    dropdownMode: false,
  },
)
const isCellHovered = ref(false)
const { fetchUsers: apiFetchUsers, insertUserSorted, filterUsers } = useFetchUsers()
const { t } = useI18n()
const themeVars = useThemeVars()
const vTooltip = tooltipDirective
const ownerSelectRef = ref<typeof NSelect | null>(null)
const isFetchingAvailableUsers = ref(false)
const userOptions = ref<SelectOption[]>([])

const options = computed(() => {
  if (!props.owner) return userOptions.value
  else if (!userOptions.value?.length) return [props.owner]
  return userOptions.value.find((user) => user.value === props.owner?.value)
    ? userOptions.value
    : insertUserSorted(userOptions.value as Owner[], props.owner)
})

async function onEdit() {
  userOptions.value = []
  emit('update:is-editing', true)
}

async function onUserSelect(v: User['id']) {
  const selectedUser = userOptions.value.find((u) => u.value === v)
  if (!selectedUser) {
    console.warn('Selected user not found in options')
    return
  }

  emit('update:owner', {
    value: selectedUser.value,
    name: selectedUser.name,
    email: selectedUser.email,
  } as Owner)
}

async function fetchUsers() {
  isFetchingAvailableUsers.value = true
  userOptions.value = await apiFetchUsers(props.myUser)
  isFetchingAvailableUsers.value = false
}

watch(
  () => props.isEditing,
  async (is, was) => {
    if (is && !was) {
      await nextTick()
      ownerSelectRef.value?.$el?.querySelector('input')?.click()
      fetchUsers()
    }
  },
)

onMounted(() => {
  if (props.dropdownMode) {
    fetchUsers()
  }
})
</script>
